<template>
    <v-form ref="form">
        <div class="intv-section">
            <div class="d-flex">
                <div>
                    <div class="intv-section-title">Students</div>
                    <div class="intv-section-summary">{{ students.length }} student{{ students.length > 1 ? 's' : '' }} in intervention</div>
                </div>
                <div class="ml-auto pt-1">
                    <fe-btn usage="secondary" @click="go('students')" data-test="create-intervention-students-add">Update</fe-btn>
                </div>
            </div>
        </div>

        <div class="intv-section">
            <div class="d-flex">
                <div>
                    <div class="intv-section-title">Intervention Details</div>
                    <div class="intv-section-summary">{{details.category_rec.name}}, {{details.intervention_level_id.name}}</div>
                </div>
                <div class="ml-auto pt-1">
                    <fe-btn usage="secondary" @click="go('details')" data-test="create-intervention-interventon-details-add">Update</fe-btn>
                </div>
            </div>
        </div>

        <div class="intv-section">
            <div class="d-flex">
                <div>
                    <div class="intv-section-title">Plan Schedule</div>
                    <div v-if="schedule.frequency_cd !== 'SPECIFIC'" class="intv-section-summary">{{$dayjs(schedule.start_date).format('M/D/YY')}} &ndash; {{$dayjs(schedule.end_date).format('M/D/YY')}}{{frequency}}</div>
                    <div v-else class="intv-section-summary d-flex" style="flex-wrap: wrap">
                        <div v-for="(date, i) in schedule.specific_dates" :key="`date-${i}`">
                            {{ $dayjs(date).format('M/D/YY') }}<span v-if="i < schedule.specific_dates.length - 1" class="mr-1">,</span>
                        </div>
                    </div>
                </div>
                <div class="ml-auto pt-1">
                    <fe-btn usage="secondary" @click="go('schedule')" data-test="create-intervention-intervention-schedule-add">Update</fe-btn>
                </div>
            </div>
        </div>

        <div class="intv-section" v-if="monitorCategoryId !== 2 && monitorCategoryId !== 3">
            <div class="d-flex">
                <div>
                    <div class="intv-section-title">Progress Monitor</div>
                    <div class="intv-section-summary">{{ progressMonitorSummary }}</div>
                </div>
                <div class="ml-auto pt-1">
                    <fe-btn usage="secondary" @click="go('monitor')" data-test="create-intervention-monitoring-schedule-add">Update</fe-btn>
                </div>
            </div>
        </div>

        <div class="intv-section" v-if="monitorCategoryId === 2">
            <div class="d-flex">
                <div>
                    <div class="intv-section-title">Point Sheet</div>
                    <div class="intv-section-summary">{{ pointSheetSummary }}</div>
                </div>
                <div class="ml-auto pt-1">
                    <fe-btn usage="secondary" @click="go('monitor')" data-test="create-intervention-point-sheet-add">Update</fe-btn>
                </div>
            </div>
        </div>

        <div class="mt-5 d-flex card-bottom">
            <fe-btn @click="submit" data-test="interventions-new-save-button" :disabled="submitting"><i v-if="submitting" class="fas fa-spinner fa-spin"></i> Create Intervention</fe-btn>
        </div>
    </v-form>
</template>

<script>
    import { mapState } from 'vuex'

    export default {
        name: 'Review',
        props: {
            value: {
                default() { return {} }
            },
            students: {
                type: Array,
                default: () => []
            },
            details: {
                type: Object,
                default: () => ({})
            },
            schedule: {
                type: Object,
                default: () => ({})
            },
            monitorAssessment: {
                type: Array,
                default: () => []
            },
            monitorPointSheet: {
                type: Object,
                default: () => ({})
            },
            fastbridgeMonitor: {
                type: Object,
                default: () => ({})
            },
        },
        data() {
            return {
                submitting: false,
            }
        },
        computed: {
            ...mapState('global', ['sessionUser']),
            ...mapState('intervention', ['school']),
            frequency() {
                if (this.schedule.frequency_cd == 'WEEK') {
                    return ', weekly schedule'
                } else if (this.schedule.frequency_cd == 'BLOCK') {
                    return ', block schedule'
                } else if (this.schedule.frequency_cd == 'NOSCHED') {
                    return ', as needed'
                }
            },
            progressMonitorSummary() {
                if (this.monitorAssessment.length == 0 || this.monitorAssessment.length == 1 && this.monitorAssessment[0].sub_category_id?.id === 'not_measured') {
                    return 'Not monitored by an assessment'
                }

                return this.monitorAssessment.length + ' active measure' + (this.monitorAssessment.length > 1 ? 's' : '')
            },
            pointSheetSummary() {
                if (this.monitorPointSheet.monitors.length == 0) {
                    return 'No point sheet'
                }

                return this.monitorPointSheet.monitors.length + ' point sheet item' + (this.monitorPointSheet.monitors.length > 1 ? 's' : '')
            },
            monitorCategoryId() {
                return this.details?.intervention_type_id?.monitor_category_id
            }
        },
        methods: {
            go(step) {
                this.$emit('go', step)
            },
            submit() {
                this.submitting = true
                let start_date = this.$dayjs(this.schedule.start_date).utc().format('YYYY-MM-DD')
                let end_date = this.$dayjs(this.schedule.end_date).utc().format('YYYY-MM-DD')
                let isSpecific = this.schedule.specific_dates && this.schedule.specific_dates.length > 0
                let payload = {
                    category_id: this.details.category_id,
                    external_pm_plan_id: this.fastbridgeMonitor?.id || null, // pass to mark FB monitor as USED
                    intervention_level_id: this.details.intervention_level_id.id,
                    intervention_type_id: this.details.intervention_type_id.id,
                    name: this.details.intervention_name,
                    user_id: this.details.user_id.id,
                    secondary_user_id: this.details.secondary_interventionists.map(user => user.id),
                    students: this.students.map(r => ({ student_id: r.student_id, student_full_name: r.student_full_name })),
                    monitors: [],
                    schedule: {
                        start_date: isSpecific ? this.schedule.specific_dates[0] : start_date,
                        end_date: isSpecific ? this.schedule.specific_dates[this.schedule.specific_dates.length-1] : end_date,
                        start_time: this.schedule.start_time,
                        end_time: this.schedule.end_time,
                        frequency_cd: this.schedule.frequency_cd,
                        frequency_cnt: this.schedule.frequency_cnt,
                        secondary_cnt: this.schedule.secondary_cnt,
                        week_days: this.schedule.week_days ? this.schedule.week_days : '',
                        session_duration: this.$getTimeDiff(this.schedule.start_time, this.schedule.end_time),
                    },
                }
                if (isSpecific) {
                    if (this.details.intervention_type_id?.monitor_category_id !== 2) payload.schedule.specific_dates = this.schedule.specific_dates
                }
                if (this.details.intervention_type_id?.monitor_category_id === 2) {
                    payload.monitors = [{
                        schedule_frequency_cd: this.schedule.frequency_cd,
                        schedule_frequency_cnt: this.schedule.frequency_cnt || 1,
                        schedule_secondary_cnt: this.schedule.secondary_cnt || null,
                        schedule_start_date: isSpecific ? this.schedule.specific_dates[0] : start_date,
                        schedule_end_date: isSpecific ? this.schedule.specific_dates[this.schedule.specific_dates.length-1] : end_date,
                        default_start_time: this.schedule.start_time,
                        default_end_time: this.schedule.end_time,
                        schedule_week_days: this.schedule.week_days ? this.schedule.week_days : '',
                        monitor_user_id: this.details.user_id.id,
                        monitor_score_options: this.monitorPointSheet.monitors,
                        specific_dates: isSpecific ? this.schedule.specific_dates : null
                    }]
                } else if (this.details.intervention_type_id?.monitor_category_id === 3) {
                    payload.monitors = []
                } else {
                    this.monitorAssessment.forEach(mon => {
                        if (mon.sub_category_id.id === 'not_measured') {
                            return
                        }
                        payload.monitors.push({
                            monitor_category_id: mon.sub_category_id.category_id,
                            monitor_user_id: mon.monitor_user_id,
                            sub_category_id: mon.sub_category_id.id,
                            primary: mon.primary_flag || false,
                            goals: mon.students.map(m => ({
                                student_id: m.studentId,
                                value: m.goal,
                                assessment_grade_id: m.gradeId || null,
                                merge_ids: m.monitorIds.map(m => m) || [],
                                primary: m.primary || false,
                            })),
                            schedule_start_date: this.$dayjs(mon.start_date).utc().format('YYYY-MM-DD'),
                            schedule_end_date: this.$dayjs(mon.end_date).utc().format('YYYY-MM-DD'),
                            default_start_time: mon.start_time,
                            default_end_time: mon.end_time,
                            schedule_frequency_cd: 'WEEK',
                            schedule_week_days: mon.monitor_week_days,
                            schedule_frequency_cnt: mon.monitor_frequency_cnt,
                        })
                    })
                }

                this.$axios.post(this.$models.getUrl('interventionCreate', 'create'), payload)
                    .then(resp => {
                        let r = resp.data
                        if (!r || !r.success) {
                            return
                        }
                        const interventionPlanId = r.intervention_plan?.[0]?.id
                        this.$emit('createdPlan', interventionPlanId)
                    }).finally(() => this.submitting = false)
            },
        }
    }
</script>

<style lang="scss" scoped>
.intv-section {
    background: #FFFFFF;
    border: 1px solid #E0E1E6;
    box-sizing: border-box;
    border-radius: 5px;
    margin: 4px 4px 4px 0;
    margin-bottom: 20px;
    padding: 8px 4px;

    &-title {
        font-weight: 500;
        font-size: 16px;
        padding-top: 4px;
        color: #050F2D;
        padding-left: 16px;
        padding-bottom: 4px;
    }

    &-summary {
        font-size: 14px;
        line-height: 15px;
        padding-left: 16px;
        color: #050F2D;
    }
}
</style>
